/**
 * Компактная альтернатива полному заказу — МСК Газобетон.
 *
 * По умолчанию на экране один расчёт: весь заказ со всей его логистикой. Второй
 * вариант — «только полные машины» — прячется за одной вторичной кнопкой и
 * раскрывается предварительным просмотром, а не занимает пол-экрана карточкой.
 *
 * Своих расчётов здесь нет: экономия, объёмы и итоги берутся готовыми из
 * вариантов buildGazSendOptions. Этот модуль только решает, есть ли что
 * предлагать, и собирает уже посчитанные числа в одном месте.
 */

const positive = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

/**
 * Данные для кнопки и предварительного просмотра.
 *
 * Предлагать нечего — возвращается null, и на экране остаётся только полный
 * заказ. Так бывает, когда:
 *   - вариантов меньше двух (расчёт не готов, полных машин нет вовсе либо весь
 *     заказ и так едет полными машинами — тогда buildGazSendOptions второй
 *     вариант не собирает);
 *   - нет экономии или она невалидна;
 *   - недостающий объём нулевой;
 *   - объёмы вариантов совпадают, то есть выбирать не из чего.
 *
 * @param {Array} options — варианты из buildGazSendOptions
 * @returns {object|null}
 */
export const resolveGazCompactAlternative = (options) => {
  const list = Array.isArray(options) ? options : [];
  if (list.length < 2) {
    return null;
  }

  const partial = list.find((option) => option?.isPartialOrder) || null;
  const whole = list.find((option) => option && !option.isPartialOrder) || null;
  if (!partial || !whole) {
    return null;
  }

  const savings = positive(partial.savings);
  const shortfall = positive(partial.shortfall);
  const partialVolume = positive(partial.volume);
  const requestedVolume = positive(whole.volume);

  if (!savings || !shortfall || !partialVolume || !requestedVolume) {
    return null;
  }

  // Варианты совпали по объёму — недополучать нечего, предлагать тоже.
  if (partialVolume >= requestedVolume) {
    return null;
  }

  return { partial, whole, savings, shortfall, partialVolume, requestedVolume };
};

/**
 * Отпечаток текущих вариантов.
 *
 * Меняется вместе с товаром, производством и любым пересчётом, поэтому по нему
 * закрывается устаревший предварительный просмотр: показывать старые цифры после
 * пересчёта нельзя. Выбранный вариант входит в отпечаток — после подтверждения
 * просмотр закрывается сам.
 */
export const gazCompactAlternativeKey = (alternative, mode) => (
  alternative
    ? [
      mode,
      alternative.partial.total,
      alternative.whole.total,
      alternative.partialVolume,
      alternative.requestedVolume,
      alternative.savings,
      alternative.shortfall,
    ].join('|')
    : String(mode || '')
);

export default resolveGazCompactAlternative;
